import type { ReactNode, FC } from "react";
import { createContext, useState, useContext, useEffect } from "react";
import { cn } from "~/utils/shadcn";

// Define the item type
type Item = {
  id: string;
  name: string;
};

// Define the props for the root component
interface MultiSelectCardRootProps {
  value: string[]; // Array of selected item IDs
  onChange: (newOptions: Item[]) => void;
  className?: string;
  children: ReactNode;
}

// Define the props for the card component
interface MultiSelectCardCardProps {
  value: string; // Item ID
  children: ReactNode;
  disabled?: boolean;
  className?: string;
}

// Create a context to manage the state and logic
const MultiSelectCardContext = createContext<{
  selectedItems: Item[];
  addItem: (item: Item) => void;
  removeItem: (itemId: string) => void;
} | null>(null);

const MultiSelectCardRoot: FC<MultiSelectCardRootProps> = ({
  value,
  onChange,
  children,
  className,
}) => {
  // Convert selected IDs back to items
  const [selectedItems, setSelectedItems] = useState<Item[]>(
    value.map((id) => ({ id, name: "" }))
  );

  useEffect(() => {
    setSelectedItems(value.map((id) => ({ id, name: "" })));
  }, [value]);

  const addItem = (item: Item) => {
    setSelectedItems((prev) => [...prev, item]);
    onChange([...selectedItems, item]);
  };

  const removeItem = (itemId: string) => {
    const index = selectedItems.findIndex((item) => item.id === itemId);
    if (index > -1) {
      const newItems = [
        ...selectedItems.slice(0, index),
        ...selectedItems.slice(index + 1),
      ];
      setSelectedItems(newItems);
      onChange(newItems); // Reflect removal
    }
  };

  const contextValue = { selectedItems, addItem, removeItem };

  return (
    <div className={cn(className, "flex gap-4 overflow-auto scrollbar-hide")}>
      <MultiSelectCardContext.Provider value={contextValue}>
        {children}
      </MultiSelectCardContext.Provider>
    </div>
  );
};

const MultiSelectCardCard: FC<MultiSelectCardCardProps> = ({
  value,
  children,
  disabled,
  className,
}) => {
  const context = useContext(MultiSelectCardContext);
  if (!context) {
    throw new Error(
      "MultiSelectCardCard must be used within a MultiSelectCardRoot"
    );
  }
  const { addItem, removeItem, selectedItems } = context;
  const itemCount = selectedItems.filter((item) => item.id === value).length;
  const isSelected = itemCount > 0;

  return (
    <button
      disabled={disabled}
      className={cn(
        className,
        "relative flex-shrink-0 select-none overflow-clip rounded-lg border transition-all cursor-pointer py-3",
        "hover:bg-secondary/80",
        isSelected
          ? "bg-secondary text-secondary-foreground border-secondary"
          : "border-input bg-background text-accent-foreground hover:bg-accent",
        "disabled:bg-muted disabled:text-muted-foreground disabled:border-muted disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50"
      )}
      onClick={() => addItem({ id: value, name: String(children) })}
    >
      {isSelected && (
        <span className="absolute inset-y-0 right-14 items-center flex justify-center my-1 text-base text-white bg-black/90 z-20 px-2 py-0.5 rounded-md">
          x{itemCount}
        </span>
      )}
      <span className="py-3 px-6 text-base text-white opacity-75">
        {children}
      </span>
      {isSelected && (
        <button
          className="absolute right-0 inset-y-0 text-lg text-white px-5 font-bold bg-black/90 transition-all z-20"
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering the card's onClick
            removeItem(value);
          }}
        >
          -
        </button>
      )}
    </button>
  );
};

// Export the components
export const MultiSelectCard = {
  Root: MultiSelectCardRoot,
  Card: MultiSelectCardCard,
};
